import React, { useContext, useEffect, useRef } from "react";
import { makeStyles, Box, Chip } from "@material-ui/core";
import Pagination from "@mui/material/Pagination";

import CaseListTopBar from "./CaseListTopBar";
import CaseCards from "../../components/CaseCards";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import {
  LoadingSkeleton,
  EmptyState,
} from "../../components/shared/helpers/StatePrimitives";
import { viewportContext } from "../../context/ViewportContext";
import CaseTable from "../../components/CaseTable";
import {
  CaseListProvider,
  useCaseListContext,
} from "../../context/CaseListContext";
import { useCaseSearch } from "../../hooks/Cases/useCaseSearch";
import { useCaseFilters } from "../../hooks/Cases/useCaseFilters";
import { useCaseSort } from "../../hooks/Cases/useCaseSort";

import { useCaseListPersistence } from "../../hooks/Cases/useCaseListPersistence";
import { tokens } from "../../theme/tokens";

const useStyle = makeStyles((theme) => ({
  pageWrap: {
    padding: tokens.space[3],
    backgroundColor: tokens.color.surface.sunken,
    minHeight: "100%",
  },
  panel: {
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  toolbarWrap: {
    borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  gridBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: tokens.space[3],
    padding: tokens.space[3],
  },
  tableBody: {
    overflowX: "auto",
  },
  paginationContainer: {
    padding: tokens.space[3],
    borderTop: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    justifyContent: "center",
    display: "flex",
  },
  paginator: {
    "& .MuiPaginationItem-root": {
      color: tokens.color.text.primary,
      borderRadius: tokens.radius.sm,
    },
    "& .MuiPaginationItem-root:hover": {
      backgroundColor: tokens.color.surface.sunken,
    },
    "& .MuiPaginationItem-root.Mui-selected": {
      backgroundColor: tokens.color.brand.primaryAction,
      color: tokens.color.brand.onPrimary,
      fontWeight: tokens.type.denseStrong.weight,
      "&:hover": { backgroundColor: tokens.color.brand.primaryActionHover },
    },
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.space[2],
    padding: `${tokens.space[2]} ${tokens.space[3]}`,
  },
  chip: {
    backgroundColor: tokens.color.surface.subtle,
    border: "none",
    borderRadius: tokens.radius.sm,
    color: tokens.color.text.primary,
    fontSize: tokens.type.caption.size,
    "& .MuiChip-deleteIcon": {
      color: tokens.color.text.secondary,
      "&:hover": { color: tokens.color.text.primary },
    },
  },
}));

function CaseListContent() {
  const classes = useStyle();

  const { tallScreen } = useContext(viewportContext) as {
    tallScreen?: boolean;
  };
  const listInnerRef = useRef();

  const {
    list,

    loaderState,
    caseLoader,
    caseView,
    page,
    setPage,
    noOfPages,
    filters,
  } = useCaseListContext();

  const { handleSearchSubmit } = useCaseSearch();
  const { handleRemoveFilter } = useCaseFilters();
  useCaseSort();

  const { isHydrated } = useCaseListPersistence();

  const view = "all";
  const hasInitialFetchRun = useRef(true);

  const handleChange = (event: any, value: number) => {
    setPage(value);
  };
  // Initial fetch - only after hydration is complete
  useEffect(() => {
    if (!isHydrated) return; // Wait for persistence to load

    if (!hasInitialFetchRun.current) {
      // Only fetch if cache was empty
      if (list?.length === 0) {
        handleSearchSubmit();
      } else {
        console.log("Using cached data, skipping initial fetch");
      }
      hasInitialFetchRun.current = true;
    }
  }, [isHydrated]); // Only depend on hydration status

  // Auto-fetch when page or filters change (after hydration)
  useEffect(() => {
    if (!isHydrated || !hasInitialFetchRun.current) return;

    console.log("Page or filters changed, fetching");
    handleSearchSubmit();
  }, [page, filters, handleSearchSubmit, isHydrated]);

  if (loaderState) {
    return (
      <div className={classes.pageWrap}>
        <div className={classes.panel}>
          <Box
            role="status"
            aria-busy="true"
            aria-label="Loading cases"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: tokens.space[2],
              padding: tokens.space[3],
            }}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <LoadingSkeleton key={i} height={tokens.density.row} />
            ))}
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageWrap}>
      <div className={classes.panel}>
        <div className={classes.toolbarWrap}>
          <CaseListTopBar />

          {filters?.length ? (
            <div className={classes.chipRow}>
              {filters.map((filter, key) => (
                <Chip
                  key={key}
                  size="small"
                  className={classes.chip}
                  label={filter.key + ": " + filter.value}
                  onDelete={() => handleRemoveFilter(key)}
                />
              ))}
            </div>
          ) : null}
        </div>

        {list?.length ? (
          caseView === "table" ? (
            <div className={classes.tableBody} ref={listInnerRef as any}>
              <CaseTable caseList={list} />
            </div>
          ) : (
            <div className={classes.gridBody} ref={listInnerRef as any}>
              {list.map((e, i) => (
                <CaseCards key={i} e={e} view={view} caseLoader={caseLoader} />
              ))}
            </div>
          )
        ) : (
          <EmptyState
            icon={<InboxOutlinedIcon />}
            title="No Items were returned"
            description="Adjust your search or filters to see cases."
          />
        )}

        {noOfPages > 0 ? (
          <div className={classes.paginationContainer}>
            <Pagination
              count={noOfPages}
              page={page}
              onChange={handleChange}
              defaultPage={1}
              size="large"
              showFirstButton
              showLastButton
              classes={{ ul: classes.paginator }}
            />
        </div>
      ) : (
        <></>
      )}
      </div>
    </div>
  );
}

function CaseList() {
  return (
    <CaseListProvider>
      <CaseListContent />
    </CaseListProvider>
  );
}

export default CaseList;
